import { useState } from "react";
import { Link } from "react-router-dom";
import TextArea from "../../shared/TextArea";
import Button from "../../shared/Button";
import {
  APP_TYPES,
  getProductTerminology,
} from "../../constants/product-terminology";
import ConfirmCard from "./ConfirmCard";
import ReportTable from "./ReportTable";

/**
 * Chat surface. It does not know whether it sits in a drawer or a column.
 *
 * @param {object} props
 */
export default function AssistantPanel({
  appType = APP_TYPES.TRUCKING,
  messages = [],
  pendingActions = [],
  sending = false,
  error = "",
  onSend,
  onConfirm,
  onCancel,
}) {
  const [draft, setDraft] = useState("");
  const terminology = getProductTerminology(appType);
  const recordLabel =
    appType === APP_TYPES.TRUCKING ? "load" : terminology.jobLower;

  const submit = () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft("");
    onSend(text);
  };

  return (
    <div className="flex h-[calc(100vh-4.5rem)] flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {messages.length === 0 ? (
          <p className="text-sm text-gray-500">
            Ask about a {recordLabel}, a driver, or a report. Refer to stops by
            name. Don&apos;t paste street addresses.
          </p>
        ) : null}
        {messages.map((message, index) => (
          <div key={index} className="space-y-2">
            <div
              className={
                message.role === "user"
                  ? "ml-8 rounded-lg bg-primary-50 px-3 py-2 text-sm text-gray-900"
                  : "mr-8 rounded-lg bg-white px-3 py-2 text-sm text-gray-900"
              }
            >
              {message.content}
              {message.result?.recordPath ? (
                <div className="mt-2">
                  <Link
                    className="text-primary-700 underline"
                    to={message.result.recordPath}
                  >
                    View {recordLabel}
                  </Link>
                </div>
              ) : null}
            </div>
            {(message.reports || []).map((report, reportIndex) => (
              <ReportTable key={reportIndex} report={report} />
            ))}
          </div>
        ))}
        {pendingActions.map((action) => (
          <ConfirmCard
            key={String(action._id)}
            action={action}
            busy={sending}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        ))}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </div>
      <div className="border-t border-gray-200 p-3">
        <TextArea
          id="assistant-draft"
          rows={3}
          value={draft}
          placeholder={`Ask about a ${recordLabel}`}
          onChange={(event) => setDraft(event.target.value)}
        />
        <div className="mt-2 flex justify-end">
          <Button type="primary" size="sm" disabled={sending || !draft.trim()} onClick={submit}>
            {sending ? "Sending" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}
